// covered-by: frontends/carrie/src/components/memory-citation/__tests__/memoryLabels.test.ts

/**
 * What a saved memory says about itself, in words a reviewer reads.
 *
 * The raw slugs (`brand_identity`, `ask_puntt_conversation`) are what warhol
 * stores; showing one to a reader tells them nothing. A slug this file does not
 * know still reads: a type is title-cased, and an origin it cannot name is left
 * out rather than printed raw.
 */

import { type TFunction } from "i18next";

import { type MemoryCitation } from "../../utils/memoryCitationFields";

const TYPE_KEY: Record<string, string> = {
  brand_identity: "askPuntt.memoryCitation.type.brandIdentity",
  institutional_context: "askPuntt.memoryCitation.type.institutionalContext",
  decision_log: "askPuntt.memoryCitation.type.decisionLog",
  terminology_glossary: "askPuntt.memoryCitation.type.terminologyGlossary",
  mistake_registry: "askPuntt.memoryCitation.type.mistakeRegistry",
  approval_precedent: "askPuntt.memoryCitation.type.approvalPrecedent",
  regulatory_compliance: "askPuntt.memoryCitation.type.regulatoryCompliance",
};

const ORIGIN_KEY: Record<string, string> = {
  ask_puntt_conversation: "askPuntt.memoryCitation.origin.askPunttConversation",
  integration_task: "askPuntt.memoryCitation.origin.integrationTask",
};

const REVIEW_ORIGIN_KEY = "askPuntt.memoryCitation.origin.reviewJudgment";

export function titleCaseSlug(slug: string): string {
  return slug
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/** The type as a label; "" when the memory has none. */
export function memoryTypeLabel(
  type: string | undefined,
  t: TFunction<"ui">,
): string {
  const slug = (type ?? "").trim();
  if (!slug) return "";
  const key = TYPE_KEY[slug];
  return key ? t(key) : titleCaseSlug(slug);
}

/**
 * Where Puntt learned the memory; "" when the source is not one it can name.
 *
 * why a pattern for the review case: the source of a memory made from a reviewer's
 * decision is `review_judgment` today, and a sibling slug for the same thing
 * ("review_confirm", "reviewer_rejection") should read the same.
 */
export function memoryOriginLabel(
  origin: string | undefined,
  t: TFunction<"ui">,
): string {
  const slug = (origin ?? "").trim().toLowerCase();
  if (!slug) return "";
  const key = ORIGIN_KEY[slug];
  if (key) return t(key);
  return /review|judg/.test(slug) ? t(REVIEW_ORIGIN_KEY) : "";
}

const EN_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * "12 Jun 2026", or "12 Jun" with `{ year: false }`. Read in UTC so the day a
 * memory was saved does not move with the reader's time zone; English reads
 * day-first to match the product's dates.
 */
export function formatMemoryDate(
  iso: string | undefined,
  language: string,
  { year = true }: { year?: boolean } = {},
): string {
  if (!iso) return "";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  // why the base language: the detector reports "en-US" for most English readers, and day-first is the approved wording ("12 Jun 2026") for all of them.
  const english = language.split("-")[0] === "en";
  const locale = english ? "en-GB" : language;
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      ...(year ? { year: "numeric" as const } : {}),
      timeZone: "UTC",
    }).formatToParts(when);
    // why a fixed month for English: en-GB prints September as "Sept" (and may differ between engines), the one month that breaks the three-letter shape.
    return parts
      .map((part) =>
        english && part.type === "month"
          ? EN_MONTHS[when.getUTCMonth()]
          : part.value,
      )
      .join("");
  } catch {
    return "";
  }
}

export interface MemoryMeta {
  /** "Brand fact", or "" */
  type: string;
  /** "12 Jun 2026", or "" */
  date: string;
  /** "Learned from an Ask Puntt chat", or "" */
  origin: string;
}

export function memoryMeta(
  memory: Pick<MemoryCitation, "type" | "createdAt" | "origin">,
  t: TFunction<"ui">,
  language: string,
): MemoryMeta {
  return {
    type: memoryTypeLabel(memory.type, t),
    date: formatMemoryDate(memory.createdAt, language),
    origin: memoryOriginLabel(memory.origin, t),
  };
}

/** The opening words of a text, cut at a whole word. */
export function clipAtWord(text: string, max = 64): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  // why the space check: a cut inside a word would print half of it.
  const atSpace = clean.charAt(max) === " ";
  const body = atSpace ? cut : cut.slice(0, Math.max(cut.lastIndexOf(" "), 0));
  return `${(body || cut).replace(/[\s,;:.—-]+$/, "")}…`;
}
